/*
 * Rename asset (modal contents) Component
 *
 */
import {useState} from 'react';
import PropTypes from 'prop-types';
import Locale from '../util/Locale';
import './Rename.scss';
import LoadingIndicator from './LoadingIndicator';
import Util from '../util/Util';
import {useAssets} from '../Providers/AssetsProvider';

const defFn = () => true;
function Rename({title = '', asset: initialAsset, onClose = defFn, onEditAsset = defFn}) {
    const [asset, setAsset] = useState(initialAsset);
    const [isLoading, setIsLoading] = useState(false);
    const [labelError, setLabelError] = useState(null);
    const [status, setStatus] = useState(null);
    const {pageDispatch} = useAssets();

    const onInput = event => {
        const {name, value} = event.target;
        if (name === 'label') {
            const label = value.trim();
            const error = label.length ? '' : Locale.getText('RENAME_NOT_EMPTY');
            setAsset(prevAsset => ({...prevAsset, label}));
            setLabelError(error);
        } else if (name === 'altText') {
            const altText = value.trim();
            setAsset(prevAsset => ({...prevAsset, altText}));
        }
    };

    const renderLabelResult = statusArg => {
        switch (statusArg) {
            case true:
                return Locale.getText('UPDATE_LABEL_SUCCESS');
            case false:
                return Locale.getText('UPDATE_LABEL_FAILED');
            default:
                return Locale.getText('UPDATE_LABEL_UNCHANGED');
        }
    };

    const renderAltResult = statusArg => {
        switch (statusArg) {
            case true:
                return Locale.getText('UPDATE_ALT_SUCCESS');
            case false:
                return Locale.getText('UPDATE_ALT_FAILED');
            default:
                return Locale.getText('UPDATE_ALT_UNCHANGED');
        }
    };

    const renderResult = () => {
        if (status) {
            return (
                <div className="status alt-description">
                    <ul>
                        <li>{renderLabelResult(status.label)}</li>
                        <li>{renderAltResult(status.alt)}</li>
                    </ul>
                </div>
            );
        }
        return null;
    };

    const renderFooter = () => {
        if (status === null) {
            return (
                <div className="rename-actions">
                    <nsemble-button key="CANCEL" text onClick={() => onClose && onClose(false)}>
                        {Locale.getText('CANCEL')}
                    </nsemble-button>
                    <LoadingIndicator isLoading={isLoading}>
                        <nsemble-button
                            key="EDIT_ASSET"
                            text
                            disabled={labelError ? true : undefined}
                            onClick={async event => {
                                if (onEditAsset && asset) {
                                    const button = event.currentTarget;
                                    setIsLoading(true);
                                    button.disabled = true;
                                    const result = await onEditAsset(asset);
                                    button.disabled = false;
                                    setStatus(result);
                                    setIsLoading(false);
                                    if (result.label || result.alt) {
                                        pageDispatch({type: 'UPDATE_ASSETS', command: 'UPDATE', assets: [asset]});
                                    }
                                }
                            }}
                        >
                            {Locale.getText('EDIT_ASSET')}
                        </nsemble-button>
                    </LoadingIndicator>
                </div>
            );
        }
        return (
            <div className="rename-actions">
                <nsemble-button key="CLOSE" text onClick={() => onClose && onClose(false)}>
                    {Locale.getText('CLOSE')}
                </nsemble-button>
            </div>
        );
    };

    return (
        <div>
            <div id="modal-header">
                <h1>{title}</h1>
            </div>
            <div id="modal-body">
                <div className="rename-file">
                    <div className="rename-section">
                        <span className="rename-title">{Locale.getText('ASSET_NAME')}</span>
                        <nsemble-input
                            name="label"
                            disabled={status === null ? undefined : true}
                            error={labelError}
                            onInput={onInput}
                            value={asset && asset.label ? Util.decodeLabel(asset.label) : ''}
                        />
                    </div>
                    <div className="rename-section">
                        <span className="rename-title">{Locale.getText('ALT_TEXT')}</span>
                        <nsemble-input
                            name="altText"
                            disabled={status === null ? undefined : true}
                            onInput={onInput}
                            value={asset ? Util.decodeLabel(asset.altText, '') : ''}
                        />
                    </div>
                    {!status && <div className="alt-description">{Locale.getText('ALT_DESCRIPTION')}</div>}
                    {renderResult()}
                </div>
            </div>
            <div id="modal-footer">{renderFooter()}</div>
        </div>
    );
}
Rename.propTypes = {
    title: PropTypes.string,
    asset: PropTypes.shape({
        altText: PropTypes.string,
        label: PropTypes.string
    }).isRequired,
    onClose: PropTypes.func,
    onEditAsset: PropTypes.func
};

export default Rename;
